import React from 'react';
import { motion } from 'framer-motion';
import { ServiceCard } from '../components/PaymentFlow';
import { CheckIcon, XIcon } from 'lucide-react';
export const Services: React.FC = () => {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  const services = [{
    id: 'basic',
    title: 'Basic Business Exit',
    price: 'POA',
    features: ['Simple company transfer', 'Limited debts (under £50k)', 'No major creditor issues', 'No ongoing leases', 'Standard documentation', 'Email support', '30-day completion target']
  }, {
    id: 'urgent',
    title: 'Urgent Rescue',
    price: 'POA',
    recommended: true,
    features: ['Expedited company transfer', 'Medium debts (£50k-£150k)', 'Some creditor arrears', 'Simple lease commitments', 'Priority documentation', 'WhatsApp & email support', '14-day completion target']
  }, {
    id: 'complex',
    title: 'Complex Rescue',
    price: 'POA',
    features: ['Comprehensive company transfer', 'Significant debts (£150k+)', 'Substantial creditor issues', 'Multiple lease commitments', 'Complex documentation', 'Priority support via all channels', 'Tailored completion timeline']
  }];
  const comparisonData = [{
    feature: 'Company Transfer',
    basic: true,
    urgent: true,
    complex: true
  }, {
    feature: 'Debt Level Handled',
    basic: 'Up to £50k',
    urgent: 'Up to £150k',
    complex: 'Over £150k'
  }, {
    feature: 'Creditor Issues',
    basic: false,
    urgent: 'Basic arrears',
    complex: 'Complex issues'
  }, {
    feature: 'Lease Commitments',
    basic: false,
    urgent: 'Simple leases',
    complex: 'Multiple leases'
  }, {
    feature: 'Creditor Pressure',
    basic: 'Low',
    urgent: 'Medium',
    complex: 'High'
  }, {
    feature: 'Completion Time',
    basic: '30 days',
    urgent: '14 days',
    complex: 'Tailored'
  }, {
    feature: 'Support Channels',
    basic: 'Email only',
    urgent: 'Email & WhatsApp',
    complex: 'All channels'
  }];
  return <div className="bg-white" data-id="element-506">
      {/* Hero Section */}
      <section className="bg-black text-white py-16 md:py-24" data-id="element-507">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-508">
          <motion.div className="max-w-3xl mx-auto text-center" initial="hidden" animate="visible" variants={fadeIn} data-id="element-509">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" data-id="element-510">
              Services & Pricing
            </h1>
            <p className="text-xl text-gray-300" data-id="element-511">
              Transparent pricing based on the complexity of your business
              situation. Select the package that best matches your needs.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Service Explanation */}
      <section className="py-16 md:py-20 bg-white" data-id="element-512">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-513">
          <motion.div className="max-w-3xl mx-auto" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-514">
            <h2 className="text-3xl font-bold mb-6" data-id="element-515">How Our Service Works</h2>
            <div className="prose prose-lg max-w-none" data-id="element-516">
              <p data-id="element-517">
                If you own a Ltd company that's drowning in debt, bills, and
                overwhelming overheads, we can help you find a way forward.
              </p>
              <p data-id="element-518">
                <strong data-id="element-519">Debt Rescue</strong> specialises in sourcing third-party
                buyers who are willing to take your company off your hands.
                These buyers will:
              </p>
              <ul data-id="element-520">
                <li data-id="element-521">Take on all existing company liabilities</li>
                <li data-id="element-522">Assume full responsibility for the business</li>
                <li data-id="element-523">Handle the challenges that come with the company</li>
              </ul>
              <p data-id="element-524">
                In return for this service, you pay a sourcing fee that varies
                based on the complexity of your situation. This approach allows
                you to walk away from your business problems and start fresh
                without the burden of company debt.
              </p>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 my-8" data-id="element-525">
                <h3 className="text-xl font-bold mb-2" data-id="element-526">Important Note</h3>
                <p className="text-gray-700" data-id="element-527">
                  This service is specifically for limited company owners. We do
                  not provide advice on personal debt, sole trader businesses,
                  or partnerships. Our service is not insolvency practitioner
                  work and we do not provide financial advice.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Pricing Cards */}
      <section className="py-12 md:py-16 bg-gray-50" data-id="element-528">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-529">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-530">
            <h2 className="text-3xl font-bold mb-4" data-id="element-531">Our Pricing Options</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-532">
              Choose the package that best fits your company's situation.
            </p>
          </motion.div>
          <motion.div className="grid md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-533">
            {services.map(service => <ServiceCard key={service.id} service={service} data-id="element-534" />)}
          </motion.div>
        </div>
      </section>
      {/* Comparison Table */}
      <section className="py-16 md:py-24 bg-white" data-id="element-535">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-536">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-537">
            <h2 className="text-3xl font-bold mb-4" data-id="element-538">Service Comparison</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-539">
              Compare our packages to find the right solution for your business.
            </p>
          </motion.div>
          <motion.div className="overflow-x-auto" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-540">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-541">
              <thead className="bg-gray-50" data-id="element-542">
                <tr data-id="element-543">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-544">
                    Feature
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-545">
                    Basic Business Exit
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-red-50" data-id="element-546">
                    Urgent Rescue
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-547">
                    Complex Rescue
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-548">
                {comparisonData.map((item, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-549">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-550">
                      {item.feature}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-551">
                      {typeof item.basic === 'boolean' ? item.basic ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-552" /> : <XIcon className="h-5 w-5 text-red-500" data-id="element-553" /> : item.basic}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 bg-red-50" data-id="element-554">
                      {typeof item.urgent === 'boolean' ? item.urgent ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-555" /> : <XIcon className="h-5 w-5 text-red-500" data-id="element-556" /> : item.urgent}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-557">
                      {typeof item.complex === 'boolean' ? item.complex ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-558" /> : <XIcon className="h-5 w-5 text-red-500" data-id="element-559" /> : item.complex}
                    </td>
                  </tr>)}
                <tr data-id="element-560">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-561">
                    Price
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-562">
                    POA
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 bg-red-50" data-id="element-563">
                    POA
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-564">
                    POA
                  </td>
                </tr>
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>
      {/* Payment Information */}
      <section className="py-16 md:py-20 bg-gray-50" data-id="element-565">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-566">
          <motion.div className="max-w-3xl mx-auto" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} data-id="element-567">
            <h2 className="text-3xl font-bold mb-6" data-id="element-568">Get Your Quote</h2>
            <div className="bg-white p-8 rounded-lg shadow-sm" data-id="element-569">
              <h3 className="text-xl font-bold mb-4" data-id="element-570">Pricing on Application</h3>
              <div className="space-y-6" data-id="element-571">
                <p className="text-gray-700" data-id="element-572">
                  We provide personalised quotes based on your specific
                  situation. Contact us via your preferred method to discuss
                  your requirements and receive a tailored quote.
                </p>
                <div className="bg-gray-50 p-4 rounded border border-gray-200" data-id="element-573">
                  <h4 className="font-bold text-lg mb-2" data-id="element-574">
                    What We'll Need to Know:
                  </h4>
                  <ul className="space-y-2 text-gray-700" data-id="element-575">
                    <li data-id="element-576">• Total amount of company debts and liabilities</li>
                    <li data-id="element-577">• Types of creditors involved</li>
                    <li data-id="element-578">• Any lease commitments or ongoing contracts</li>
                    <li data-id="element-579">• Timeline requirements</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};